import { useState, useEffect, useCallback } from 'react';
import api, { downloadFile, downloadFilePost } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, CalendarIcon, MoreHorizontal, FileText, FileOutput, ShieldCheck, Wallet, Shield, KeyRound } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { format } from 'date-fns';

const DEPARTMENTS = ['Engineering', 'Sales', 'Marketing', 'HR', 'Finance', 'Operations', 'Design', 'Support'];

const emptyForm = {
  name: '', email: '', password: '', employee_id: '', department: '',
  designation: '', date_of_joining: null, reporting_to: '', location: '',
  basic_salary: '', hra: '', allowances: '',
};

function EmployeeDialog({ open, onClose, onSave, employee, isEdit }) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (employee && isEdit) {
      setForm({
        ...employee,
        date_of_joining: employee.date_of_joining ? new Date(employee.date_of_joining) : null,
        basic_salary: employee.basic_salary?.toString() || '',
        hra: employee.hra?.toString() || '',
        allowances: employee.allowances?.toString() || '',
        password: '',
      });
    } else {
      setForm(emptyForm);
    }
  }, [employee, isEdit, open]);

  const handleSave = async () => {
    if (!form.name || !form.email || !form.employee_id || !form.department || !form.designation) {
      toast.error('Please fill required fields');
      return;
    }
    if (!isEdit && !form.password) {
      toast.error('Password is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name, email: form.email, employee_id: form.employee_id,
        department: form.department, designation: form.designation,
        date_of_joining: form.date_of_joining ? format(form.date_of_joining, 'yyyy-MM-dd') : '',
        reporting_to: form.reporting_to, location: form.location,
        basic_salary: parseFloat(form.basic_salary) || 0,
        hra: parseFloat(form.hra) || 0,
        allowances: parseFloat(form.allowances) || 0,
      };
      if (!isEdit) payload.password = form.password;
      await onSave(payload);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            {isEdit ? 'Update employee details below.' : 'Fill in the details to add a new employee.'}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Name *</Label>
            <Input value={form.name} onChange={e => set('name', e.target.value)} className="rounded-sm" data-testid="emp-name-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Email *</Label>
            <Input value={form.email} onChange={e => set('email', e.target.value)} type="email" className="rounded-sm" data-testid="emp-email-input" disabled={isEdit} />
          </div>
          {!isEdit && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Password *</Label>
              <Input value={form.password} onChange={e => set('password', e.target.value)} type="password" className="rounded-sm" data-testid="emp-password-input" />
            </div>
          )}
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Employee ID *</Label>
            <Input value={form.employee_id} onChange={e => set('employee_id', e.target.value)} className="rounded-sm" data-testid="emp-id-input" disabled={isEdit} />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Department *</Label>
            <Select value={form.department} onValueChange={v => set('department', v)}>
              <SelectTrigger className="rounded-sm" data-testid="emp-department-select"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {DEPARTMENTS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Designation *</Label>
            <Input value={form.designation} onChange={e => set('designation', e.target.value)} className="rounded-sm" data-testid="emp-designation-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Date of Joining</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className={`w-full justify-start text-left font-normal rounded-sm ${!form.date_of_joining ? 'text-muted-foreground' : ''}`} data-testid="emp-doj-picker">
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {form.date_of_joining ? format(form.date_of_joining, 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar mode="single" selected={form.date_of_joining} onSelect={d => set('date_of_joining', d)} />
              </PopoverContent>
            </Popover>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Reporting To</Label>
            <Input value={form.reporting_to} onChange={e => set('reporting_to', e.target.value)} className="rounded-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Location</Label>
            <Input value={form.location} onChange={e => set('location', e.target.value)} className="rounded-sm" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Basic Salary</Label>
            <Input value={form.basic_salary} onChange={e => set('basic_salary', e.target.value)} type="number" className="rounded-sm font-mono" data-testid="emp-salary-input" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">HRA</Label>
            <Input value={form.hra} onChange={e => set('hra', e.target.value)} type="number" className="rounded-sm font-mono" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-widest text-muted-foreground">Allowances</Label>
            <Input value={form.allowances} onChange={e => set('allowances', e.target.value)} type="number" className="rounded-sm font-mono" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="rounded-sm">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="emp-save-btn">
            {saving ? 'Saving...' : (isEdit ? 'Update' : 'Add Employee')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEmp, setEditEmp] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [relievingEmp, setRelievingEmp] = useState(null);
  const [relievingDate, setRelievingDate] = useState(null);
  const [relievingNotes, setRelievingNotes] = useState('');
  const [letterLoading, setLetterLoading] = useState('');
  const [kycEmp, setKycEmp] = useState(null);
  const [kycData, setKycData] = useState(null);
  const [kycLoading, setKycLoading] = useState(false);
  const [salaryEmp, setSalaryEmp] = useState(null);
  const [allTemplates, setAllTemplates] = useState([]);
  const [appliedTemplates, setAppliedTemplates] = useState([]);
  const [selectedTemplates, setSelectedTemplates] = useState([]);
  const [salaryLoading, setSalaryLoading] = useState(false);
  const [resetPwEmp, setResetPwEmp] = useState(null);
  const [resetPwValue, setResetPwValue] = useState('');

  const fetchEmployees = useCallback(() => {
    api.get('/employees/all/users')
      .then(res => setEmployees(res.data))
      .catch(() => toast.error('Failed to load employees'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const handleAdd = async (data) => {
    await api.post('/auth/register', data);
    toast.success('Employee added');
    fetchEmployees();
  };

  const handleEdit = async (data) => {
    await api.put(`/employees/${editEmp.id}`, data);
    toast.success('Employee updated');
    setEditEmp(null);
    fetchEmployees();
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/employees/${deleteId}`);
      toast.success('Employee deleted');
      setDeleteId(null);
      fetchEmployees();
    } catch { toast.error('Delete failed'); }
  };

  const handleOfferLetter = async (emp) => {
    setLetterLoading(emp.id);
    try {
      await downloadFile(`/letters/offer/${emp.id}`, `offer_letter_${emp.employee_id}.pdf`);
      toast.success('Offer letter downloaded');
    } catch {
      toast.error('Failed to generate offer letter');
    } finally {
      setLetterLoading('');
    }
  };

  const handleViewKyc = async (emp) => {
    setKycEmp(emp);
    setKycLoading(true);
    try {
      const res = await api.get(`/kyc/${emp.id}`);
      setKycData(res.data);
    } catch {
      toast.error('Failed to load KYC details');
    } finally {
      setKycLoading(false);
    }
  };

  const handleViewSalaryComponents = async (emp) => {
    setSalaryEmp(emp);
    setSalaryLoading(true);
    try {
      const [templatesRes, appliedRes] = await Promise.all([
        api.get('/salary-components/templates'),
        api.get(`/salary-components/employee/${emp.id}`)
      ]);
      setAllTemplates(templatesRes.data);
      setAppliedTemplates(appliedRes.data);
      setSelectedTemplates(appliedRes.data.map(t => t.id));
    } catch {
      toast.error('Failed to load salary components');
    } finally {
      setSalaryLoading(false);
    }
  };

  const handleApplyTemplates = async () => {
    setSalaryLoading(true);
    try {
      await api.post(`/salary-components/employee/${salaryEmp.id}/apply`, {
        template_ids: selectedTemplates
      });
      toast.success('Salary components updated');
      setSalaryEmp(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply templates');
    } finally {
      setSalaryLoading(false);
    }
  };

  const toggleTemplate = (templateId) => {
    setSelectedTemplates(prev => 
      prev.includes(templateId) 
        ? prev.filter(id => id !== templateId)
        : [...prev, templateId]
    );
  };

  const handleToggleAdmin = async (emp) => {
    const newRole = emp.role === 'admin' ? 'employee' : 'admin';
    const action = newRole === 'admin' ? 'promote' : 'demote';
    
    if (!window.confirm(`Are you sure you want to ${action} ${emp.name} to ${newRole}?`)) {
      return;
    }
    
    try {
      await api.put(`/employees/${emp.id}/role`, { role: newRole });
      toast.success(`${emp.name} ${action}d to ${newRole}`);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action} user`);
    }
  };

  const handleResetPassword = async () => {
    if (!resetPwValue || resetPwValue.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/employees/${resetPwEmp.id}/reset-password`, { new_password: resetPwValue });
      toast.success(`Password reset for ${resetPwEmp.name}`);
      setResetPwEmp(null);
      setResetPwValue('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reset failed');
    }
  };

  const handleRelievingLetter = async () => {
    if (!relievingDate) {
      toast.error('Please select last working date');
      return;
    }
    setLetterLoading(relievingEmp.id);
    try {
      await downloadFilePost(
        `/letters/relieving/${relievingEmp.id}`,
        { last_working_date: format(relievingDate, 'yyyy-MM-dd'), notes: relievingNotes },
        `relieving_letter_${relievingEmp.employee_id}.pdf`
      );
      toast.success('Relieving letter downloaded');
      setRelievingEmp(null);
      setRelievingDate(null);
      setRelievingNotes('');
    } catch {
      toast.error('Failed to generate relieving letter');
    } finally {
      setLetterLoading('');
    }
  };

  const filtered = employees.filter(e =>
    e.name?.toLowerCase().includes(search.toLowerCase()) ||
    e.employee_id?.toLowerCase().includes(search.toLowerCase()) ||
    e.department?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div data-testid="employees-page">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h2 className="text-2xl font-heading font-bold text-foreground tracking-tight">Employees</h2>
          <p className="text-sm text-muted-foreground font-body mt-1">{employees.length} total employees</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="add-employee-btn">
          <Plus className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add Employee
        </Button>
      </div>

      <div className="relative mb-4 max-w-xs">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
        <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search employees..." className="pl-10 rounded-sm" data-testid="employee-search" />
      </div>

      <div className="border border-border bg-card rounded-sm overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-body text-xs uppercase tracking-widest">ID</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest">Name</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest">Email</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest">Department</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest">Designation</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest">Location</TableHead>
              <TableHead className="font-body text-xs uppercase tracking-widest text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">Loading...</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-8">No employees found</TableCell></TableRow>
            ) : filtered.map(emp => (
              <TableRow key={emp.id} data-testid={`employee-row-${emp.employee_id}`}>
                <TableCell className="font-mono text-xs">{emp.employee_id}</TableCell>
                <TableCell className="font-medium">
                  <span className="flex items-center gap-2">
                    {emp.name}
                    {emp.role === 'admin' && (
                      <Badge className="text-[10px] bg-violet-100 text-violet-700 border-violet-200">Admin</Badge>
                    )}
                  </span>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">{emp.email}</TableCell>
                <TableCell><Badge variant="secondary" className="text-xs rounded-sm">{emp.department}</Badge></TableCell>
                <TableCell className="text-sm">{emp.designation}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{emp.location}</TableCell>
                <TableCell className="text-right">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" data-testid={`emp-actions-${emp.employee_id}`}>
                        <MoreHorizontal className="w-4 h-4" strokeWidth={1.5} />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => setEditEmp(emp)} data-testid={`edit-emp-${emp.employee_id}`}>
                        <Pencil className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Edit Details
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => handleOfferLetter(emp)} disabled={letterLoading === emp.id} data-testid={`offer-letter-${emp.employee_id}`}>
                        <FileText className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        {letterLoading === emp.id ? 'Generating...' : 'Offer Letter'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => setRelievingEmp(emp)} data-testid={`relieving-letter-${emp.employee_id}`}>
                        <FileOutput className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Relieving Letter
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleViewKyc(emp)} data-testid={`view-kyc-${emp.employee_id}`}>
                        <ShieldCheck className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        View KYC
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleViewSalaryComponents(emp)} data-testid={`salary-components-${emp.employee_id}`}>
                        <Wallet className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Salary Components
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => handleToggleAdmin(emp)} data-testid={`toggle-admin-${emp.employee_id}`}>
                        <Shield className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        {emp.role === 'admin' ? 'Remove Admin Rights' : 'Make Admin'}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { setResetPwEmp(emp); setResetPwValue(''); }} data-testid={`reset-pw-${emp.employee_id}`}>
                        <KeyRound className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Reset Password
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => setDeleteId(emp.id)} className="text-red-600 focus:text-red-600" data-testid={`delete-emp-${emp.employee_id}`}>
                        <Trash2 className="w-4 h-4 mr-2" strokeWidth={1.5} />
                        Delete Employee
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <EmployeeDialog open={dialogOpen} onClose={() => setDialogOpen(false)} onSave={handleAdd} isEdit={false} />
      <EmployeeDialog open={!!editEmp} onClose={() => setEditEmp(null)} onSave={handleEdit} employee={editEmp} isEdit={true} />

      {/* Delete Confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Delete Employee</DialogTitle>
            <DialogDescription>This action cannot be undone. All related data will be removed.</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 rounded-sm" data-testid="confirm-delete-btn">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Relieving Letter Dialog */}
      <Dialog open={!!relievingEmp} onOpenChange={() => { setRelievingEmp(null); setRelievingDate(null); setRelievingNotes(''); }}>
        <DialogContent className="rounded-sm max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Generate Relieving Letter</DialogTitle>
            <DialogDescription>
              For {relievingEmp?.name} ({relievingEmp?.employee_id})
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Last Working Date *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className={`w-full justify-start text-left font-normal rounded-sm ${!relievingDate ? 'text-muted-foreground' : ''}`} data-testid="relieving-date-picker">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {relievingDate ? format(relievingDate, 'PPP') : 'Select date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={relievingDate} onSelect={setRelievingDate} />
                </PopoverContent>
              </Popover>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Additional Notes (Optional)</Label>
              <Input value={relievingNotes} onChange={e => setRelievingNotes(e.target.value)} placeholder="e.g., Resigned on good terms" className="rounded-sm" data-testid="relieving-notes-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setRelievingEmp(null); setRelievingDate(null); setRelievingNotes(''); }} className="rounded-sm">Cancel</Button>
            <Button onClick={handleRelievingLetter} disabled={!!letterLoading} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="generate-relieving-btn">
              {letterLoading ? 'Generating...' : 'Generate & Download'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* KYC View Dialog */}
      <Dialog open={!!kycEmp} onOpenChange={() => { setKycEmp(null); setKycData(null); }}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">KYC Details - {kycEmp?.name}</DialogTitle>
            <DialogDescription>Employee ID: {kycEmp?.employee_id}</DialogDescription>
          </DialogHeader>
          {kycLoading ? (
            <p className="text-sm text-muted-foreground py-4">Loading...</p>
          ) : kycData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Bank Name</p>
                  <p className="font-medium text-foreground">{kycData.bank_name || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Account Number</p>
                  <p className="font-mono text-foreground">{kycData.bank_account_number || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">IFSC Code</p>
                  <p className="font-mono text-foreground">{kycData.ifsc_code || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">PAN Number</p>
                  <p className="font-mono text-foreground">{kycData.pan_number || '-'}</p>
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest">Aadhaar Number</p>
                  <p className="font-mono text-foreground">{kycData.aadhaar_number || '-'}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="border border-border rounded-sm p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">PAN Document</p>
                  {kycData.pan_document ? (
                    <div>
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs mb-1">Uploaded</Badge>
                      <p className="text-xs text-muted-foreground truncate">{kycData.pan_document.original_name}</p>
                    </div>
                  ) : (
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Not uploaded</Badge>
                  )}
                </div>
                <div className="border border-border rounded-sm p-3">
                  <p className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Aadhaar Document</p>
                  {kycData.aadhaar_document ? (
                    <div>
                      <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs mb-1">Uploaded</Badge>
                      <p className="text-xs text-muted-foreground truncate">{kycData.aadhaar_document.original_name}</p>
                    </div>
                  ) : (
                    <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">Not uploaded</Badge>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground py-4">No KYC data submitted yet</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Salary Components Dialog */}
      <Dialog open={!!salaryEmp} onOpenChange={() => { setSalaryEmp(null); setAllTemplates([]); setAppliedTemplates([]); setSelectedTemplates([]); }}>
        <DialogContent className="rounded-sm max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">Salary Components - {salaryEmp?.name}</DialogTitle>
            <DialogDescription>
              Select salary component templates to apply to this employee.
            </DialogDescription>
          </DialogHeader>
          {salaryLoading ? (
            <p className="text-sm text-muted-foreground py-4">Loading...</p>
          ) : allTemplates.length === 0 ? (
            <div className="py-6 text-center">
              <p className="text-sm text-muted-foreground mb-2">No salary templates created yet.</p>
              <p className="text-xs text-muted-foreground">Go to Salary Components page to create templates first.</p>
            </div>
          ) : (
            <div className="space-y-3 max-h-[400px] overflow-y-auto">
              <div className="text-xs text-muted-foreground mb-2">
                {selectedTemplates.length} template(s) selected
              </div>
              {allTemplates.map(t => (
                <div 
                  key={t.id}
                  className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer transition-colors ${
                    selectedTemplates.includes(t.id) 
                      ? 'border-[#002FA7] bg-blue-50/50' 
                      : 'border-border hover:border-border'
                  }`}
                  onClick={() => toggleTemplate(t.id)}
                  data-testid={`template-option-${t.id}`}
                >
                  <Checkbox 
                    checked={selectedTemplates.includes(t.id)} 
                    onCheckedChange={() => toggleTemplate(t.id)}
                    className="mt-0.5"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">{t.name}</span>
                      <Badge 
                        className={`text-[10px] rounded-sm ${
                          t.type === 'earning' 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200' 
                            : 'bg-red-50 text-red-700 border-red-200'
                        }`}
                      >
                        {t.type}
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5 font-mono">
                      {t.is_percentage ? `${t.amount}% of ${t.percentage_of?.replace('_', ' ')}` : `₹${t.amount.toLocaleString()}`}
                    </p>
                    {t.description && (
                      <p className="text-xs text-muted-foreground mt-1 truncate">{t.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSalaryEmp(null)} className="rounded-sm">Cancel</Button>
            <Button 
              onClick={handleApplyTemplates} 
              disabled={salaryLoading || allTemplates.length === 0} 
              className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" 
              data-testid="apply-salary-components-btn"
            >
              {salaryLoading ? 'Applying...' : 'Apply Templates'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!resetPwEmp} onOpenChange={() => { setResetPwEmp(null); setResetPwValue(''); }}>
        <DialogContent className="rounded-sm max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading">Reset Password</DialogTitle>
            <DialogDescription>Set a new password for {resetPwEmp?.name} ({resetPwEmp?.employee_id})</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">New Password</Label>
              <Input
                type="password"
                value={resetPwValue}
                onChange={e => setResetPwValue(e.target.value)}
                placeholder="Min 6 characters"
                className="rounded-sm"
                data-testid="reset-pw-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetPwEmp(null); setResetPwValue(''); }} className="rounded-sm">Cancel</Button>
            <Button onClick={handleResetPassword} className="bg-[#002FA7] hover:bg-[#002585] rounded-sm" data-testid="confirm-reset-pw-btn">
              Reset Password
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
